import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Post, PostData } from "@/components/Community/Post";
import { PostSkeleton } from "@/components/Community/PostSkeleton";
import { LatestJobs } from "@/components/Community/LatestJobs";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/ui/time-input";
import {
  ArrowLeft,
  Rss,
  FileText,
  Calendar,
  Briefcase,
  Clock,
  MapPin,
  Users,
  X,
  Edit2,
  UserCheck,
  UserMinus,
  ChevronDown,
  ChevronUp,
  Share2,
  Send,
  Headphones,
} from "lucide-react";
import { LinkedinIcon as Linkedin } from "@/components/icons/LinkedinIcon";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AppState } from "@/store";
import { extractHashtags, getCommunityPath } from "@/utils/community";
import ShadCNDatePicker from "@/components/ui/datePicker";
import Tiptap, { TiptapRef } from "@/components/Tiptap";
// import { CommentSection } from '@/components/Community/CommentSection';
// import { MessageCircle } from 'lucide-react';
import {
  getEventById,
  updatePost,
  reactPost,
  removeReactPost,
  createComment,
  updateComment,
  deleteComment,
  getCommentsByPost,
  registerEvent,
  unregisterEvent,
  cancelEvent as cancelEventAPI,
  sharePost,
  getPostById,
} from "@/api/community";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { getSupportNotificationCount } from "@/components/Community/SupportPage";

export function EventDetails() {
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();

  const [post, setPost] = useState<PostData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const previousEventIdRef = useRef<string | undefined>(undefined);

  // Get commentId and replyId from URL query params
  const commentId = searchParams.get("commentId");
  const replyId = searchParams.get("replyId");

  const userData = useSelector((state: AppState) => state.auth.userData);
  const user = userData?.user;
  const [supportReportCount] = useState(getSupportNotificationCount());

  // UI state
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [showAttendeesExpanded, setShowAttendeesExpanded] = useState(false);
  // const [showComments, setShowComments] = useState(!!commentId);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showUnregisterDialog, setShowUnregisterDialog] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Edit state
  const [editEventTitle, setEditEventTitle] = useState("");
  const [editEventDate, setEditEventDate] = useState<Date | undefined>(undefined);
  const [editEventTime, setEditEventTime] = useState("");
  const [editEventEndTime, setEditEventEndTime] = useState("");
  const [editEventLocation, setEditEventLocation] = useState("");
  const [editEventMaxParticipants, setEditEventMaxParticipants] = useState("");
  const [editEventDescription, setEditEventDescription] = useState("");
  const [editEventMaxParticipantsError, setEditEventMaxParticipantsError] = useState("");
  const [editEventTimeError, setEditEventTimeError] = useState("");
  const editDescriptionEditorRef = useRef<TiptapRef>(null);

  // Helper function to transform API comments to PostData format
  const transformCommentsToPostFormat = (apiComments: any[]): any[] => {
    return apiComments.map((comment) => ({
      id: comment.id || comment._id,
      author: {
        id: comment.author?.id || comment.author_id,
        name: comment.author?.full_name || t("community.comments.unknown_user"),
        avatar: comment.author?.photo_thumbnail_url || comment.author?.photo || "",
        title: comment.author?.job_title
          ? comment.author.job_title
          : comment.author?.type === "job_seeker"
            ? t("job_seeker")
            : comment.author?.type === "employer"
              ? t("employer")
              : "",
        type: comment.author?.type,
        organizationId: comment.author?.organization_id,
      },
      content: comment.content,
      timestamp: new Date(comment.created_at),
      isEdited: Array.isArray(comment.edit_history) && comment.edit_history.length > 0,
      editedAt: comment.edit_history?.[0]?.edited_at
        ? new Date(comment.edit_history[0].edited_at)
        : undefined,
      likes: [],
      replies: comment.replies ? transformCommentsToPostFormat(comment.replies) : [],
    }));
  };

  // Fetch event by ID
  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        setError(t("community.events.event_id_not_found"));
        setIsLoading(false);
        return;
      }

      // Only show loading skeleton when eventId changes, not when commentId/replyId change
      const isEventIdChanged = previousEventIdRef.current !== eventId;
      if (isEventIdChanged) {
        setIsLoading(true);
      }
      previousEventIdRef.current = eventId;

      try {
        setError(null);

        const apiPost = await getPostById(eventId);

        // Check if this is a shared post with an original event
        const isSharedEvent = apiPost.type === "shared" && apiPost.original_post?.type === "event";
        const eventPost = isSharedEvent ? apiPost.original_post : apiPost;

        // Verify this is actually an event post
        if (!eventPost.is_event && eventPost.type !== "event") {
          setError(t("community.events.not_event_post"));
          setIsLoading(false);
          return;
        }

        // Fetch comments for this event post
        let comments: any[] = [];
        try {
          if (eventPost.comments_count > 0) {
            const commentsData = await getCommentsByPost(eventPost.id, 1, 50);
            comments = transformCommentsToPostFormat(commentsData.comments);
          }
        } catch (error) {
          console.error(`Error fetching comments for event ${eventPost.id}:`, error);
        }

        const transformedPost: PostData = {
          ...eventPost,
          timestamp: new Date(eventPost.created_at),
          isEdited: eventPost.edit_history && eventPost.edit_history.length > 0,
          editedAt: eventPost.edit_history?.[0]?.edited_at
            ? new Date(eventPost.edit_history[0].edited_at)
            : undefined,
          likes: [],
          comments,
          shared_post: undefined as any,
          isEvent: true,
          eventDetails: eventPost.event_details
            ? {
                title: eventPost.event_details.title,
                date: new Date(eventPost.event_details.date),
                endDate: eventPost.event_details.end_date
                  ? new Date(eventPost.event_details.end_date)
                  : undefined,
                location: eventPost.event_details.location,
                maxParticipants: eventPost.event_details.max_participants,
                description: eventPost.event_details.description,
              }
            : undefined,
          eventStatus: eventPost.event_status as "active" | "cancelled" | undefined,
          eventParticipants: eventPost.event_participants
            ? eventPost.event_participants
                .filter(
                  (participant: any) => participant.user !== null && participant.user !== undefined,
                )
                .map((participant: any) => ({
                  userId: participant.user.id,
                  userName: participant.user.full_name,
                  userTitle:
                    participant.user.job_title ||
                    (participant.user.type === "job_seeker" ? t("job_seeker") : t("employer")),
                  userType: participant.user.type,
                  organizationId: participant.user.organization_id,
                  photoUrl: participant.user.photo_thumbnail_url || participant.user.photo,
                  registeredAt: new Date(participant.registered_at),
                }))
            : [],
        };

        setPost(transformedPost);
      } catch (err: any) {
        console.error("Error fetching event:", err);

        // Check if it's a 404 error
        if (err?.response?.status === 404 || err?.error?.code === 404) {
          setError(t("community.events.event_not_found"));
        } else {
          setError(t("community.events.failed_load_event"));
        }
      } finally {
        // Only set loading to false if we set it to true (eventId changed)
        if (isEventIdChanged) {
          setIsLoading(false);
        }
      }
    };

    fetchEvent();
  }, [eventId, commentId, replyId]);

  const handleLike = async (postId: string, emoji: string) => {
    if (!user?.id || !post) return;

    const existingLike = post.likes?.find((like) => like.userId === user.id);

    try {
      if (existingLike && existingLike.emoji === emoji) {
        // Same emoji clicked - unlike the post
        await removeReactPost(postId);
        // Update local state optimistically
        const newLikes = post.likes?.filter((like) => like.userId !== user.id) || [];
        setPost({
          ...post,
          likes: newLikes,
          likes_count: Math.max(0, post.likes_count - 1),
        });
      } else {
        // Different emoji or new like
        await reactPost(postId, { emoji });
        // Update local state optimistically
        const newLikes = post.likes?.filter((like) => like.userId !== user.id) || [];
        const isNewReaction = !existingLike;
        setPost({
          ...post,
          likes: [
            ...newLikes,
            { userId: user.id, userName: user.full_name || t("community.events.unknown"), emoji },
          ],
          likes_count: isNewReaction ? post.likes_count + 1 : post.likes_count,
        });
      }
    } catch (error) {
      console.error("Error updating reaction:", error);
      toast({
        title: t("community.events.failed_reaction"),
        description: t("community.events.failed_reaction_desc"),
        variant: "destructive",
      });
    }
  };

  const handleComment = async (
    postId: string,
    comment: string,
    mentionedUserIds: string[] = [],
  ) => {
    if (!post) return;

    try {
      const newComment = await createComment({
        post_id: postId,
        content: comment,
        mentioned_users: mentionedUserIds,
      });

      // Transform the API comment to UI format
      const transformedComment = transformCommentsToPostFormat([newComment])[0];

      // Update local state with the new comment
      setPost({
        ...post,
        comments: [...(post.comments || []), transformedComment],
        comments_count: post.comments_count + 1,
      });

      toast({
        title: t("community.events.comment_added"),
        description: t("community.events.comment_added_desc"),
      });
    } catch (error) {
      console.error("Error creating comment:", error);
      toast({
        title: t("community.events.failed_add_comment"),
        description: t("community.events.failed_add_comment_desc"),
        variant: "destructive",
      });
    }
  };

  const handleShare = async (postId: string) => {
    if (!user?.id) return;

    try {
      await sharePost(postId, {
        share_content: "",
      });

      toast({
        title: t("community.events.event_shared_feed"),
        description: t("community.events.event_shared_feed_desc"),
      });
    } catch (error) {
      console.error("Error sharing event:", error);
      toast({
        title: t("community.events.failed_share_event"),
        description: t("community.events.failed_share_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleEdit = async (
    postId: string,
    updates: { content: string; media_urls: string[]; tagged_users: string[] },
  ) => {
    if (!post) return;

    try {
      const hashtags = extractHashtags(updates.content);

      await updatePost(postId, {
        content: updates.content,
        visibility: "public",
        hashtag_names: hashtags,
        media_urls: updates.media_urls,
        tagged_users: updates.tagged_users,
      });

      // Fetch the updated post to get fresh data
      const freshPost = await getEventById(postId);

      // Update the post with fresh data, keeping existing comments and likes
      setPost({
        ...post,
        content: freshPost.content,
        media_urls: freshPost.media_urls,
        "media_urls:raw": freshPost["media_urls:raw"],
        tagged_users: freshPost.tagged_users,
        hashtags: freshPost.hashtags,
        isEdited: true,
        editedAt: new Date(freshPost.updated_at),
        edit_history: freshPost.edit_history,
        updated_at: freshPost.updated_at,
      });

      toast({
        title: t("community.events.event_post_updated"),
        description: t("community.events.event_post_updated_desc"),
      });
    } catch (error: any) {
      console.error("Error updating event post:", error);
      toast({
        title: t("community.events.failed_update_post"),
        description: error.response?.data?.message || t("community.events.failed_update_post_desc"),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (
    postId: string,
    reason?: string,
    postType?: string,
    isEvent?: boolean,
  ) => {
    try {
      // EventDetails is specifically for events, so always treat as event post
      // For event posts, use updatePost API to set visibility to private
      await updatePost(postId, {
        content: post?.content || "",
        visibility: "private",
      });

      toast({
        title: t("community.events.event_post_deleted"),
        description: t("community.events.event_post_deleted_desc"),
      });

      // Navigate back to the events page
      navigate(`${getCommunityPath(user?.type)}?section=events`);
    } catch (error) {
      console.error("Error deleting event post:", error);
      toast({
        title: t("community.events.failed_delete_post"),
        description: t("community.events.failed_delete_post_desc"),
        variant: "destructive",
      });
    }
  };

  const handleEditComment = async (
    _postId: string,
    commentId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => {
    if (!post) return;

    try {
      await updateComment(commentId, { content: newContent, mentioned_users: mentionedUserIds });

      // Update local state optimistically
      setPost({
        ...post,
        comments: post.comments?.map((comment) =>
          comment.id === commentId
            ? { ...comment, content: newContent, isEdited: true, editedAt: new Date() }
            : comment,
        ),
      });

      toast({
        title: t("community.events.comment_updated"),
        description: t("community.events.comment_updated_desc"),
      });
    } catch (error) {
      console.error("Error updating comment:", error);
      toast({
        title: t("community.events.failed_update_comment"),
        description: t("community.events.failed_update_comment_desc"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async (_postId: string, commentId: string) => {
    if (!post) return;

    try {
      await deleteComment(commentId, "Deleted by user");

      // Update local state optimistically
      setPost({
        ...post,
        comments: post.comments?.filter((comment) => comment.id !== commentId),
        comments_count: Math.max(0, post.comments_count - 1),
      });

      toast({
        title: t("community.events.comment_deleted"),
        description: t("community.events.comment_deleted_desc"),
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast({
        title: t("community.events.failed_delete_comment"),
        description: t("community.events.failed_delete_comment_desc"),
        variant: "destructive",
      });
    }
  };

  const handleAddReply = async (
    postId: string,
    commentId: string,
    reply: string,
    mentionedUserIds: string[] = [],
  ) => {
    // CommentSection handles reply creation and state update directly
    // This handler is intentionally empty to avoid duplicate API calls
    // The reply is already created and managed by CommentSection.tsx
  };

  const handleEditReply = async (
    _postId: string,
    commentId: string,
    replyId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => {
    if (!post) return;

    try {
      await updateComment(replyId, { content: newContent, mentioned_users: mentionedUserIds });

      // Update local state optimistically
      setPost({
        ...post,
        comments: post.comments?.map((comment) => {
          if (comment.id === commentId) {
            return {
              ...comment,
              replies: comment.replies.map((reply: any) =>
                reply.id === replyId
                  ? { ...reply, content: newContent, isEdited: true, editedAt: new Date() }
                  : reply,
              ),
            };
          }
          return comment;
        }),
      });

      toast({
        title: t("community.events.reply_updated"),
        description: t("community.events.reply_updated_desc"),
      });
    } catch (error) {
      console.error("Error updating reply:", error);
      toast({
        title: t("community.events.failed_update_reply"),
        description: t("community.events.failed_update_reply_desc"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteReply = async (_postId: string, commentId: string, replyId: string) => {
    if (!post) return;

    try {
      await deleteComment(replyId, "Deleted by user");

      // Update local state optimistically
      setPost({
        ...post,
        comments: post.comments?.map((comment) => {
          if (comment.id === commentId) {
            return {
              ...comment,
              replies: comment.replies.filter((reply: any) => reply.id !== replyId),
            };
          }
          return comment;
        }),
      });

      toast({
        title: t("community.events.reply_deleted"),
        description: t("community.events.reply_deleted_desc"),
      });
    } catch (error) {
      console.error("Error deleting reply:", error);
      toast({
        title: t("community.events.failed_delete_reply"),
        description: t("community.events.failed_delete_reply_desc"),
        variant: "destructive",
      });
    }
  };

  const handleJoinEvent = async (postId: string) => {
    if (!user?.id || !post) return;

    const alreadyRegistered = post.eventParticipants?.some((p) => p.userId === user.id);
    if (alreadyRegistered) {
      toast({
        title: t("community.events.already_registered"),
        description: t("community.events.already_registered_desc"),
        variant: "destructive",
      });
      return;
    }

    if (
      post.eventDetails?.maxParticipants &&
      (post.eventParticipants?.length || 0) >= post.eventDetails.maxParticipants
    ) {
      toast({
        title: t("community.events.event_full"),
        description: t("community.events.event_full_desc"),
        variant: "destructive",
      });
      return;
    }

    try {
      await registerEvent(postId);

      const newParticipant = {
        userId: user.id,
        userName: user.full_name || t("community.events.unknown"),
        userTitle: user.type === "employer" ? t("employer") : t("job_seeker"),
        userType: user.type,
        organizationId: user.organization_id,
        photoUrl: user.photo_thumbnail_url || user.photo,
        registeredAt: new Date(),
      };

      setPost({
        ...post,
        eventParticipants: [...(post.eventParticipants || []), newParticipant],
      });

      toast({
        title: t("community.events.successfully_registered"),
        description: t("community.events.successfully_registered_desc"),
      });
    } catch (error) {
      console.error("Error joining event:", error);
      toast({
        title: t("community.events.failed_join_event"),
        description: t("community.events.failed_join_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleLeaveEvent = async (postId: string) => {
    if (!user?.id || !post) return;

    try {
      await unregisterEvent(postId);

      setPost({
        ...post,
        eventParticipants: (post.eventParticipants || []).filter((p) => p.userId !== user.id),
      });

      toast({
        title: t("community.events.successfully_left_event"),
        description: t("community.events.successfully_left_event_desc"),
      });
    } catch (error) {
      console.error("Error leaving event:", error);
      toast({
        title: t("community.events.failed_leave_event"),
        description: t("community.events.failed_leave_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleCancelEvent = async (postId: string) => {
    if (!post) return;

    try {
      await cancelEventAPI(postId);

      setPost({
        ...post,
        eventStatus: "cancelled" as const,
      });

      toast({
        title: t("community.events.event_cancelled_notify"),
        description: t("community.events.event_cancelled_notify_desc"),
      });
    } catch (error) {
      console.error("Error cancelling event:", error);
      toast({
        title: t("community.events.failed_cancel_event"),
        description: t("community.events.failed_cancel_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleEditEvent = async (
    postId: string,
    eventDetails: {
      title: string;
      date: Date;
      endDate?: Date;
      location: string;
      maxParticipants?: number;
      description?: string;
    },
  ) => {
    if (!post) return;

    try {
      const payload: any = {
        content: post.content,
        event_status: post.eventStatus === "cancelled" ? "cancelled" : "active",
        event_details: {
          title: eventDetails.title,
          date: eventDetails.date.toISOString(),
          location: eventDetails.location,
          ...(eventDetails.endDate && { end_date: eventDetails.endDate.toISOString() }),
          ...(eventDetails.maxParticipants && { max_participants: eventDetails.maxParticipants }),
          ...(eventDetails.description && { description: eventDetails.description }),
        },
      };

      await updatePost(postId, payload);

      setPost({
        ...post,
        eventDetails,
        isEdited: true,
        editedAt: new Date(),
      });

      toast({
        title: t("community.events.event_updated"),
        description: t("community.events.event_updated_desc"),
      });
    } catch (error) {
      console.error("Error updating event:", error);
      toast({
        title: t("community.events.failed_cancel_event"),
        description: t("community.events.failed_cancel_event_desc"),
        variant: "destructive",
      });
    }
  };

  // New handlers for card-based UI
  const handleStartEdit = () => {
    if (!post?.eventDetails) return;

    setEditEventTitle(post.eventDetails.title);
    setEditEventDate(post.eventDetails.date);
    setEditEventTime(
      `${String(post.eventDetails.date.getHours()).padStart(2, "0")}:${String(post.eventDetails.date.getMinutes()).padStart(2, "0")}`,
    );
    setEditEventEndTime(
      post.eventDetails.endDate
        ? `${String(post.eventDetails.endDate.getHours()).padStart(2, "0")}:${String(post.eventDetails.endDate.getMinutes()).padStart(2, "0")}`
        : "",
    );
    setEditEventLocation(post.eventDetails.location);
    setEditEventMaxParticipants(post.eventDetails.maxParticipants?.toString() || "");
    setEditEventDescription(post.eventDetails.description || "");
    setIsEditingEvent(true);
  };

  const handleEditMaxParticipantsChange = (value: string) => {
    setEditEventMaxParticipants(value);

    if (value && parseInt(value) > 10000) {
      setEditEventMaxParticipantsError(t("community.events.max_participants_error"));
    } else if (value && parseInt(value) < 1) {
      setEditEventMaxParticipantsError(t("community.events.min_participants_error"));
    } else {
      setEditEventMaxParticipantsError("");
    }
  };

  const validateEditEventTimes = (startTime: string, endTime: string) => {
    if (startTime && endTime) {
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);
      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;

      if (endMinutes <= startMinutes) {
        setEditEventTimeError(t("community.events.end_time_error"));
        return false;
      } else {
        setEditEventTimeError("");
        return true;
      }
    }
    setEditEventTimeError("");
    return true;
  };

  const handleEditEventTimeChange = (value: string) => {
    setEditEventTime(value);
    validateEditEventTimes(value, editEventEndTime);
  };

  const handleEditEventEndTimeChange = (value: string) => {
    setEditEventEndTime(value);
    validateEditEventTimes(editEventTime, value);
  };

  const handleSaveEventEdit = async () => {
    if (
      !post ||
      !editEventTitle.trim() ||
      !editEventDate ||
      !editEventTime ||
      !editEventLocation.trim()
    ) {
      toast({
        title: t("community.events.missing_fields_error"),
        description: t("community.events.missing_fields_error_desc"),
        variant: "destructive",
      });
      return;
    }

    if (
      editEventMaxParticipants &&
      (parseInt(editEventMaxParticipants) > 10000 || parseInt(editEventMaxParticipants) < 1)
    ) {
      return;
    }

    if (!validateEditEventTimes(editEventTime, editEventEndTime)) {
      return;
    }

    const [hours, minutes] = editEventTime.split(":").map(Number);
    const eventDate = new Date(editEventDate);
    eventDate.setHours(hours, minutes, 0, 0);

    let eventEndDate: Date | undefined = undefined;
    if (editEventEndTime) {
      const [endHours, endMinutes] = editEventEndTime.split(":").map(Number);
      eventEndDate = new Date(editEventDate);
      eventEndDate.setHours(endHours, endMinutes, 0, 0);
    }

    const eventDetails = {
      title: editEventTitle,
      date: eventDate,
      endDate: eventEndDate,
      location: editEventLocation,
      maxParticipants: editEventMaxParticipants ? parseInt(editEventMaxParticipants) : undefined,
      description: editEventDescription,
    };

    try {
      const payload: any = {
        content: post.content,
        event_status: post.eventStatus === "cancelled" ? "cancelled" : "active",
        event_details: {
          title: eventDetails.title,
          date: eventDetails.date.toISOString(),
          location: eventDetails.location,
          ...(eventDetails.endDate && { end_date: eventDetails.endDate.toISOString() }),
          ...(eventDetails.maxParticipants && { max_participants: eventDetails.maxParticipants }),
          ...(eventDetails.description && { description: eventDetails.description }),
        },
      };

      await updatePost(post.id, payload);

      setPost({
        ...post,
        eventDetails,
        isEdited: true,
        editedAt: new Date(),
      });

      setIsEditingEvent(false);
      toast({
        title: t("community.events.event_updated"),
        description: t("community.events.event_updated_desc"),
      });
    } catch (error) {
      console.error("Error updating event:", error);
      toast({
        title: t("community.events.failed_cancel_event"),
        description: t("community.events.failed_cancel_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleCancelEventEdit = () => {
    setIsEditingEvent(false);
    setEditEventMaxParticipantsError("");
    setEditEventTimeError("");
  };

  const handleUserProfileClick = (userId: string, userType?: string, organizationId?: string) => {
    const currentUserType = user?.type;

    if (userType) {
      if (currentUserType === "job_seeker") {
        if (userType === "job_seeker") {
          navigate(`/jobseeker/users/${userId}`);
        } else if (userType === "employer") {
          if (organizationId) {
            navigate(`/jobseeker/employers/${organizationId}`);
          }
        }
      } else if (currentUserType === "employer") {
        if (userType === "employer") {
          if (organizationId) {
            navigate(`/employer/organizations/${organizationId}`);
          }
        } else if (userType === "job_seeker") {
          navigate(`/employer/jobseekers/${userId}`);
        }
      }
    }
  };

  const handleBackToEvents = () => {
    navigate(`${getCommunityPath(user?.type)}?section=events`);
  };

  const handleShareToFeed = async () => {
    if (!post) return;

    try {
      await sharePost(post.id);

      navigate(getCommunityPath(user?.type));

      toast({
        title: t("community.events.shared_to_feed"),
        description: t("community.events.shared_to_feed_desc"),
      });
      setShowShareMenu(false);
    } catch (error) {
      console.error("Error sharing event:", error);
      toast({
        title: t("community.events.failed_share"),
        description: t("community.events.failed_share_desc"),
        variant: "destructive",
      });
      setShowShareMenu(false);
    }
  };

  const handleShareToLinkedIn = () => {
    if (!post) return;

    const userType = user?.type === "employer" ? "employer" : "jobseeker";
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const eventUrl = `${baseUrl}/${userType}/event/${post.id}`;
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(eventUrl)}`;
    window.open(linkedInUrl, "_blank", "noopener,noreferrer");
    setShowShareMenu(false);
  };

  const handleShareToWhatsApp = () => {
    if (!post) return;

    const userType = user?.type === "employer" ? "employer" : "jobseeker";
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const eventUrl = `${baseUrl}/${userType}/event/${post.id}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(eventUrl)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    setShowShareMenu(false);
  };

  const sidebarNavItems = [
    { id: "feed", label: t("community.post_details.nav_feed"), icon: Rss },
    { id: "my-posts", label: t("community.post_details.nav_my_posts"), icon: FileText },
    { id: "events", label: t("community.post_details.nav_events"), icon: Calendar },
    {
      id: "support",
      label: t("community.post_details.nav_support"),
      icon: Headphones,
      badge: supportReportCount,
    },
  ].filter((item) => {
    // Hide Latest Jobs for employer users
    if (item.id === "latest-jobs" && user?.type === "employer") {
      return false;
    }
    return true;
  });

  return (
    <main data-community-container className="mx-auto w-full max-w-(--breakpoint-2xl)">
      <div className="">
        {/* Mobile Navigation Pills - Visible only on mobile/tablet */}
        <div className="mb-4 xl:hidden">
          <Button variant="outline" size="sm" className="gap-2" onClick={handleBackToEvents}>
            <ArrowLeft className="h-4 w-4" />
            <span className="font-semibold">{t("community.events.back_to_events")}</span>
          </Button>
        </div>

        <div className="flex gap-4 lg:gap-6">
          {/* Left Sidebar Navigation - Fixed on desktop */}
          <aside className="hidden w-64 shrink-0 xl:block">
            <div className="fixed top-32 w-64">
              <div className="brand-gradient-deep overflow-hidden rounded-2xl border border-white/10 shadow-md">
                <nav aria-label={t("community.feed.title")} className="flex flex-col gap-1 p-3">
                  {sidebarNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.id === "events";
                    const badgeCount = "badge" in item ? item.badge : 0;

                    return (
                      <Button
                        key={item.id}
                        variant="ghost"
                        aria-current={isActive ? "page" : undefined}
                        className={`group relative h-auto w-full justify-start gap-3.5 rounded-xl px-3.5 py-3 text-sm transition-colors ${
                          isActive
                            ? "bg-white/15 font-semibold text-white hover:bg-white/15 hover:text-white"
                            : "font-medium text-white hover:bg-white/10"
                        }`}
                        onClick={() => {
                          navigate(`${getCommunityPath(user?.type)}?section=${item.id}`);
                        }}
                      >
                        {isActive && (
                          <span className="absolute top-1.5 bottom-1.5 -left-3 w-1 rounded-r-full bg-white" />
                        )}
                        <Icon className="h-5 w-5 shrink-0" />
                        <span className="truncate">{item.label}</span>
                        {badgeCount > 0 && (
                          <span className="bg-destructive ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white">
                            {badgeCount}
                          </span>
                        )}
                      </Button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="w-full min-w-0 flex-1 space-y-4 sm:space-y-8 lg:w-auto">
            <div className="space-y-1 sm:space-y-2">
              <div className="flex items-start gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden xl:flex"
                  onClick={handleBackToEvents}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="space-y-1 md:hidden">
                  <h1 className="text-foreground text-xl font-bold tracking-tight sm:text-2xl">
                    <span className="gradient-text">{t("community.events.event_details")}</span>
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    {t("community.events.view_register_event")}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-0 space-y-6">
              {isLoading ? (
                <PostSkeleton />
              ) : error ? (
                <Card className="border-border">
                  <div className="flex flex-col items-center gap-3 py-14 text-center">
                    <div className="bg-destructive/10 text-destructive flex h-12 w-12 items-center justify-center rounded-full">
                      <Calendar className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-destructive text-base font-semibold">
                        {t("community.events.error_loading_event")}
                      </p>
                      <p className="text-muted-foreground text-sm">{error}</p>
                    </div>
                    <Button onClick={handleBackToEvents} variant="outline">
                      {t("community.events.back_to_events")}
                    </Button>
                  </div>
                </Card>
              ) : post && post.eventDetails ? (
                <div className="space-y-6">
                  {/* Event Card */}
                  <Card
                    className={`${
                      post.eventStatus === "cancelled"
                        ? "border-destructive/20 bg-destructive/5"
                        : "border-border bg-card"
                    }`}
                  >
                    {isEditingEvent ? (
                      // Edit Mode
                      <>
                        <CardHeader className="pb-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="text-primary h-5 w-5" />
                            <h3 className="text-lg font-bold">
                              {t("community.events.edit_event_title")}
                            </h3>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div>
                            <label className="mb-1 block text-sm font-semibold">
                              {t("community.events.event_title")}{" "}
                              <span className="text-destructive">*</span>
                            </label>
                            <Input
                              type="text"
                              value={editEventTitle}
                              onChange={(e) => setEditEventTitle(e.target.value)}
                              placeholder={t("community.events.event_title_placeholder")}
                              className="w-full"
                            />
                          </div>

                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div className="flex flex-col gap-2">
                              <label className="block text-sm font-semibold">
                                {t("community.events.date")}{" "}
                                <span className="text-destructive">*</span>
                              </label>
                              <ShadCNDatePicker
                                value={editEventDate}
                                onChange={setEditEventDate}
                                placeholder={t("community.events.pick_date")}
                                disablePastDates
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-sm font-semibold">
                                {t("community.events.max_participants_optional")}
                              </label>
                              <Input
                                type="number"
                                value={editEventMaxParticipants}
                                onChange={(e) => handleEditMaxParticipantsChange(e.target.value)}
                                placeholder={t("community.events.max_participants_placeholder")}
                                min="1"
                                max="10000"
                                className={`w-full ${editEventMaxParticipantsError ? "border-destructive" : ""}`}
                              />
                              {editEventMaxParticipantsError && (
                                <p className="text-destructive mt-1 text-xs">
                                  {editEventMaxParticipantsError}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div>
                              <label className="mb-1 block text-sm font-semibold">
                                {t("community.events.start_time")}{" "}
                                <span className="text-destructive">*</span>
                              </label>
                              <TimeInput
                                value={editEventTime}
                                onChange={handleEditEventTimeChange}
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-sm font-semibold">
                                {t("community.events.end_time")}
                              </label>
                              <TimeInput
                                value={editEventEndTime}
                                onChange={handleEditEventEndTimeChange}
                              />
                              {editEventTimeError && (
                                <p className="text-destructive mt-1 text-xs">
                                  {editEventTimeError}
                                </p>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="mb-1 block text-sm font-semibold">
                              {t("community.events.location")}{" "}
                              <span className="text-destructive">*</span>
                            </label>
                            <Input
                              type="text"
                              value={editEventLocation}
                              onChange={(e) => setEditEventLocation(e.target.value)}
                              placeholder={t("community.events.location_placeholder")}
                              className="w-full"
                            />
                          </div>

                          <div>
                            <label className="mb-1 block text-sm font-semibold">
                              {t("community.events.description_optional")}
                            </label>
                            <Tiptap
                              ref={editDescriptionEditorRef}
                              content={editEventDescription}
                              onChange={setEditEventDescription}
                              placeholder={t("community.events.description_placeholder")}
                              enableLink={true}
                              enableAutolink={true}
                              enableMention={false}
                              enableEmoji={true}
                            />
                          </div>

                          <div className="flex gap-2 pt-2">
                            <Button onClick={handleSaveEventEdit} size="sm" className="gap-2">
                              <UserCheck className="h-4 w-4" />
                              {t("community.events.save_changes")}
                            </Button>
                            <Button
                              onClick={handleCancelEventEdit}
                              size="sm"
                              variant="ghost"
                              className="gap-2"
                            >
                              <X className="h-4 w-4" />
                              {t("community.events.cancel")}
                            </Button>
                          </div>
                        </CardContent>
                      </>
                    ) : (
                      // Display Mode
                      <>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="mb-2 flex flex-wrap items-center gap-2">
                                <div className="flex min-w-0 items-start gap-2">
                                  <Calendar className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                                  <h3 className="text-lg leading-snug font-bold">
                                    {post.eventDetails.title}
                                  </h3>
                                </div>
                                {post.isEdited && (
                                  <Badge variant="secondary" className="gap-1 text-xs">
                                    {t("community.events.updated_badge")}
                                  </Badge>
                                )}
                                {post.eventStatus === "cancelled" && (
                                  <Badge variant="destructive" className="gap-1">
                                    <X className="h-3 w-3" />
                                    {t("community.events.cancelled_badge")}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-muted-foreground text-sm">
                                {t("community.events.created_by")}{" "}
                                <span
                                  className="text-foreground cursor-pointer font-medium hover:underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (post.author) {
                                      handleUserProfileClick(
                                        post.author.id,
                                        post.author.type,
                                        post.author.organization_id,
                                      );
                                    }
                                  }}
                                >
                                  {post.author?.full_name || t("community.events.unknown")}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="secondary"
                                className="border-brand-accent-border bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent gap-1"
                              >
                                <Users className="h-3 w-3" />
                                {post.eventParticipants?.length || 0}
                                {post.eventDetails.maxParticipants &&
                                  `/${post.eventDetails.maxParticipants}`}
                              </Badge>
                              <Popover open={showShareMenu} onOpenChange={setShowShareMenu}>
                                <PopoverTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <Share2 className="h-4 w-4" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-64 rounded-xl p-1.5 shadow-lg"
                                  align="end"
                                >
                                  <div className="space-y-1">
                                    <button
                                      onClick={handleShareToFeed}
                                      className="hover:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                                    >
                                      <Rss className="text-primary h-4 w-4" />
                                      <span className="font-medium">
                                        {t("community.events.share_to_feed")}
                                      </span>
                                    </button>
                                    <button
                                      onClick={handleShareToLinkedIn}
                                      className="hover:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                                    >
                                      <Linkedin className="h-4 w-4 text-[#0A66C2]" />
                                      <span className="font-medium">
                                        {t("community.events.share_to_linkedin")}
                                      </span>
                                    </button>
                                    <button
                                      onClick={handleShareToWhatsApp}
                                      className="hover:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                                    >
                                      <Send className="h-4 w-4 text-[#25D366]" />
                                      <span className="font-medium">
                                        {t("community.events.share_to_whatsapp")}
                                      </span>
                                    </button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid gap-3">
                            <div className="flex items-center gap-3 text-sm">
                              <Clock className="text-muted-foreground h-4 w-4 shrink-0" />
                              <div>
                                <p className="font-medium">
                                  {post.eventDetails.date.toLocaleDateString("en-US", {
                                    weekday: "long",
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  })}
                                </p>
                                <p className="text-muted-foreground">
                                  {post.eventDetails.date.toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                  {post.eventDetails.endDate && (
                                    <>
                                      {" "}
                                      -{" "}
                                      {post.eventDetails.endDate.toLocaleTimeString("en-US", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3 text-sm">
                              <MapPin className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                              <p className="font-medium">{post.eventDetails.location}</p>
                            </div>
                            {post.eventDetails.description && (
                              <div
                                className="text-muted-foreground border-t pt-2 text-sm"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeHtml(post.eventDetails.description),
                                }}
                              />
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="space-y-2 pt-3">
                            {/* Owner actions - Edit and Cancel Event */}
                            {(post.author_id === user?.id || post.author?.id === user?.id) &&
                              post.eventStatus !== "cancelled" && (
                                <div className="flex gap-2">
                                  <Button
                                    onClick={handleStartEdit}
                                    variant="outline"
                                    className="flex-1 gap-2"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                    {t("community.events.edit_event")}
                                  </Button>
                                  <Button
                                    onClick={() => setShowCancelDialog(true)}
                                    variant="destructive"
                                    className="flex-1 gap-2"
                                  >
                                    <X className="h-4 w-4" />
                                    {t("community.events.cancel_event")}
                                  </Button>
                                </div>
                              )}

                            {/* Non-owner actions - Join or Unregister (only show if NOT the owner) */}
                            {post.author_id !== user?.id &&
                              post.author?.id !== user?.id &&
                              post.eventStatus !== "cancelled" && (
                                <>
                                  {post.eventParticipants?.some((p) => p.userId === user?.id) ? (
                                    <Button
                                      onClick={() => setShowUnregisterDialog(true)}
                                      variant="outline"
                                      className="border-destructive text-destructive hover:bg-destructive/10 w-full gap-2"
                                    >
                                      <UserMinus className="h-4 w-4" />
                                      {t("community.events.unregister_from_event")}
                                    </Button>
                                  ) : (
                                    <Button
                                      onClick={() => handleJoinEvent(post.id)}
                                      className="bg-primary hover:bg-primary/90 w-full gap-2"
                                      disabled={
                                        post.eventDetails.maxParticipants !== undefined &&
                                        (post.eventParticipants?.length || 0) >=
                                          post.eventDetails.maxParticipants
                                      }
                                    >
                                      <UserCheck className="h-4 w-4" />
                                      {post.eventDetails.maxParticipants !== undefined &&
                                      (post.eventParticipants?.length || 0) >=
                                        post.eventDetails.maxParticipants
                                        ? t("community.events.join_event_full")
                                        : t("community.events.join_event_text")}
                                    </Button>
                                  )}
                                </>
                              )}

                            {/* Show Attendees */}
                            {(post.eventParticipants?.length || 0) > 0 && (
                              <div className="border-t pt-3">
                                <Button
                                  variant={
                                    post.eventStatus === "cancelled" ? "destructive" : "ghost"
                                  }
                                  onClick={() => setShowAttendeesExpanded(!showAttendeesExpanded)}
                                  className="w-full justify-between"
                                >
                                  <span className="text-sm font-semibold">
                                    {t("community.events.attendees")} (
                                    {post.eventParticipants?.length || 0})
                                  </span>
                                  {showAttendeesExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </Button>

                                {showAttendeesExpanded && (
                                  <div className="mt-3 space-y-2">
                                    {post.eventParticipants?.map((participant, index) => (
                                      <div
                                        key={index}
                                        className={`flex cursor-pointer items-center gap-3 rounded-md p-2 transition-colors ${
                                          post.eventStatus === "cancelled"
                                            ? "bg-destructive/10 hover:bg-destructive/20"
                                            : "bg-muted/50 hover:bg-muted"
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUserProfileClick(
                                            participant.userId,
                                            participant.userType,
                                            participant.organizationId,
                                          );
                                        }}
                                      >
                                        <Avatar className="ring-border h-8 w-8 ring-2">
                                          <AvatarImage
                                            src={participant.photoUrl}
                                            alt={participant.userName}
                                          />
                                          <AvatarFallback className="bg-brand-accent text-primary text-xs font-semibold">
                                            {participant.userName
                                              .split(" ")
                                              .map((n) => n[0])
                                              .join("")}
                                          </AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0 flex-1">
                                          <p className="truncate text-sm font-medium">
                                            {participant.userName}
                                          </p>
                                          <p className="text-muted-foreground truncate text-xs">
                                            {participant.userTitle}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </>
                    )}
                  </Card>

                  {/* Comments Section */}
                  {/* {post && !isEditingEvent && (
                    <Card>
                      <CardHeader>
                        <Button
                          variant="ghost"
                          onClick={() => setShowComments(!showComments)}
                          className="w-full justify-between -m-4 p-4"
                        >
                          <div className="flex items-center gap-2">
                            <MessageCircle className="h-5 w-5" />
                            <span className="text-lg font-semibold">
                              Comments ({post.comments_count || 0})
                            </span>
                          </div>
                          {showComments ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </CardHeader>
                      {showComments && (
                        <CardContent>
                          <CommentSection
                            postId={post.id}
                            comments={post.comments || []}
                            postAuthor={post.author ? { id: post.author.id, name: post.author.full_name, title: post.author.job_title } : undefined}
                            onComment={handleComment}
                            onEditComment={handleEditComment}
                            onDeleteComment={handleDeleteComment}
                            onAddReply={handleAddReply}
                            onEditReply={handleEditReply}
                            onDeleteReply={handleDeleteReply}
                            autoExpandCommentId={commentId || undefined}
                            highlightReplyId={replyId || undefined}
                          />
                        </CardContent>                      )}
                    </Card>
                  )} */}

                  {/* Cancel Event Dialog */}
                  {showCancelDialog && (
                    <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {t("community.events.cancel_event_confirm")}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("community.events.cancel_event_confirm_desc")}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>
                            {t("community.events.no_keep_event")}
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              if (post) {
                                handleCancelEvent(post.id);
                                setShowCancelDialog(false);
                              }
                            }}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {t("community.events.yes_cancel_event")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}

                  {/* Unregister Dialog */}
                  {showUnregisterDialog && (
                    <AlertDialog open={showUnregisterDialog} onOpenChange={setShowUnregisterDialog}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {t("community.events.unregister_confirm")}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("community.events.unregister_confirm_desc")}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>
                            {t("community.events.no_stay_registered")}
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              if (post) {
                                handleLeaveEvent(post.id);
                                setShowUnregisterDialog(false);
                              }
                            }}
                          >
                            {t("community.events.yes_unregister")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {/* Right Sidebar - Job Posts */}
          {user?.type === "job_seeker" && (
            <aside className="hidden w-80 shrink-0 lg:block">
              <div className="">
                <LatestJobs />
              </div>
            </aside>
          )}
        </div>
      </div>
    </main>
  );
}
